import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Upload, Form, InputNumber, Select } from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ReactComponent as UploadIcon } from '../../assets/icon/upload.svg';
import { ReactComponent as TrashIcon } from '../../assets/icon/trash.svg';
import { fetchPois } from '../../state/actions/pois';
import { createRoom, updateRoom } from '../../state/actions/room';

const AddRoomModal = ({ isVisible, onCancel, onSubmit, editingRoom = null }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [pois, setPois] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const { siteId, siteName } = useParams();

  const poisData = useSelector((state) => state.pois.list);
  useEffect(() => {
    if (isVisible && siteId) {
        dispatch(fetchPois({ siteId }));
    }
  }, [isVisible, dispatch, siteId]);

  useEffect(() => {
    if (poisData) {
      const formattedPois = poisData.map((poi) => ({
        value: poi._id,
        label: poi.name,
        key: poi._id,
      }));
      setPois(formattedPois);
    }
  }, [poisData]);

  // Pre-fill form when editing
  useEffect(() => {
    if (editingRoom && isVisible) {
      form.setFieldsValue({
        name: editingRoom.name,
        associatePoi: editingRoom.poiIds && editingRoom.poiIds.length > 0
          ? editingRoom.poiIds[0]._id
          : undefined,
        capacity: editingRoom.capacity,
      });
      // Don't set uploaded file for existing rooms - they already have an image URL
      setUploadedFile(null);
    } else if (!editingRoom && isVisible) {
      // Reset form for new room
      form.resetFields();
      setUploadedFile(null);
    }
  }, [editingRoom, isVisible, form]);

  const handleCancel = () => {
    form.resetFields();
    setUploadedFile(null);
    onCancel();
  };

  const handleSubmit = async (values) => {
    try {
      const roomData = {
        name: values.name,
        siteId: siteId,
        capacity: values.capacity,
        poiIds: values.associatePoi ? [values.associatePoi] : [],
        roomImage: uploadedFile ? { file: uploadedFile } : null
      };

      if (editingRoom) {
        // Update existing room
        await dispatch(updateRoom({
          roomId: editingRoom._id,
          ...roomData
        }));
      } else {
        // Create new room
        await dispatch(createRoom(roomData));
      }

      form.resetFields();
      setUploadedFile(null);
      onSubmit(values); // Call onSubmit to refresh data
      onCancel(); // Close modal on success
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  return (
    <Modal
      title={<span style={{ fontWeight: 'bold' }}>
        {editingRoom ? 'Edit Room' : 'Add New Room'}
      </span>}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        style={{ marginTop: 20 }}
      >
        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Upload Room Image</span>}
          name="roomImage"
        >
          {uploadedFile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img
                src={URL.createObjectURL(uploadedFile)}
                alt="Room preview"
                style={{
                  width: '120px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                  {uploadedFile.name}
                </div>
                <div style={{ fontSize: '14px', color: '#1890ff', marginBottom: '8px' }}>
                  {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
              <div
                style={{
                  cursor: 'pointer',
                  color: '#ff4d4f',
                  fontSize: '18px',
                  padding: '8px'
                }}
                onClick={() => setUploadedFile(null)}
              >
                <TrashIcon />
              </div>
            </div>
          ) : editingRoom && editingRoom.image ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img
                src={editingRoom.image}
                alt="Room preview"
                style={{
                  width: '120px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                  {editingRoom.image.split('/').pop() || 'Image.PNG'}
                </div>
                {/* <div style={{ fontSize: '14px', color: '#1890ff', marginBottom: '8px' }}>
                  Current image
                </div> */}
              </div>
              <div
                style={{
                  cursor: 'pointer',
                  color: '#ff4d4f',
                  fontSize: '18px',
                  padding: '8px'
                }}
                onClick={() => {
                  // Clear the existing image by setting it to null in editingRoom
                  if (editingRoom) {
                    editingRoom.image = null;
                  }
                  setUploadedFile(null);
                }}
              >
                <TrashIcon/>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '120px',
                height: '80px',
                backgroundColor: '#f5f5f5',
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <UploadIcon/>
              </div>
              <div style={{ flex: 1 }}>
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  showUploadList={false}
                  onChange={(info) => {
                    if (info.fileList.length > 0) {
                      const file = info.fileList[0].originFileObj;
                      setUploadedFile(file);
                    } else {
                      setUploadedFile(null);
                    }
                  }}
                  accept="image/jpeg,image/jpg,image/png"
                >
                  <div style={{ cursor: 'pointer' }}>
                    <div style={{ fontSize: '16px', marginBottom: '4px', fontWeight: 'bold' }}>
                      <span style={{ color: '#9E9E9E' }}>Drag here or </span>
                      <span style={{ color: '#64D5D2', textDecoration: 'underline' }}>
                        Browse
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#9E9E9E' }}>
                      File format supported: jpg, png, mp4 (Up to 50MB)
                    </div>
                    <div style={{ fontSize: '12px', color: '#9E9E9E' }}>
                      Dimention 1:1
                    </div>
                  </div>
                </Upload>
              </div>
            </div>
          )}
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Name</span>}
          name="name"
          rules={[{ required: true, message: 'Please enter room name!' }]}
        >
          <Input placeholder="Enter room's name" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Associate POI</span>}
          name="associatePoi"
        >
          <Select
            placeholder="Select POI"
            options={pois}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Capacity</span>}
          name="capacity"
        >
          <InputNumber
            placeholder="Enter number of capacity"
            style={{ width: '100%' }}
            min={1}
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 30, textAlign: 'right' }}>
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" style={{backgroundColor: '#64D5D2'}}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddRoomModal;
